import { type ComponentPropsWithoutRef, type FC, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import InboxIcon from '@/assets/svg/ui-elements/inbox.svg';

import Button from '../Button';
import {
  StyledDescription,
  StyledEmptyState,
  StyledIcon,
  StyledText,
  StyledTitle,
} from './EmptyState.styled';
import type { EmptyStateAction, EmptyStateProps, EmptyStateVariant } from './types';

type ResolvedProps = Omit<ComponentPropsWithoutRef<'div'>, 'title'> & {
  variant?: EmptyStateVariant;
  description?: ReactNode;
  action?: EmptyStateAction;
};

const EmptyState: FC<EmptyStateProps> = ({ title, icon, showIcon = true, ...props }) => {
  const { variant = 'primary', description, action, ...rest } = props as ResolvedProps;

  return (
    <StyledEmptyState $variant={variant} {...rest}>
      {showIcon && (
        <StyledIcon $variant={variant} aria-hidden="true">
          {icon ?? <InboxIcon />}
        </StyledIcon>
      )}

      <StyledText>
        <StyledTitle $variant={variant}>{title}</StyledTitle>
        {description && <StyledDescription>{description}</StyledDescription>}
      </StyledText>

      {action && (
        <Button
          size="md"
          variant="solid"
          color="primary"
          onClick={action.onClick}
          {...(action.to !== undefined && {
            nativeButton: false,
            role: 'link',
            render: <RouterLink to={action.to} />,
          })}
        >
          {action.label}
        </Button>
      )}
    </StyledEmptyState>
  );
};

EmptyState.displayName = 'EmptyState';

export default EmptyState;
